import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Clock, Package, Phone, Mail, User,
  MessageSquare, Plus, Trash2, ExternalLink, Send, FileText, Pencil, Copy, Check
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function PaymentBadge({ status }) {
  if (status === 'paid') return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">Pago</span>;
  if (status === 'failed') return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">Falhado</span>;
  if (status === 'refunded') return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">Reembolsado</span>;
  return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-600">Pendente</span>;
}

function OrderBadge({ status }) {
  if (status === 'completed') return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">Entregue</span>;
  if (status === 'processing') return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">A processar</span>;
  if (status === 'cancelled') return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">Cancelado</span>;
  return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-600">Pendente</span>;
}

function getProfileUrl(platform, username) {
  if (!username) return null;
  const u = username.replace('@', '');
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (platform === 'instagram') return `https://www.instagram.com/${u}`;
  if (platform === 'tiktok') return `https://www.tiktok.com/@${u}`;
  if (platform === 'youtube') return `https://www.youtube.com/@${u}`;
  if (platform === 'facebook') return `https://www.facebook.com/${u}`;
  return null;
}

function formatPhone(phone) {
  if (!phone) return phone;
  const n = String(phone).replace(/\D/g, '');
  if (n.length === 9) return `${n.slice(0,3)} ${n.slice(3,6)} ${n.slice(6)}`;
  if (n.length === 12 && n.startsWith('351')) return `+351 ${n.slice(3,6)} ${n.slice(6,9)} ${n.slice(9)}`;
  return phone;
}


function CopyUsernameBtn({ username }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(getDisplayName(username));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} title="Copiar @username" className="text-gray-400 hover:text-blue-600 transition">
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function getDisplayName(username) {
  if (!username) return '';
  if (username.startsWith('http://') || username.startsWith('https://')) {
    try {
      const clean = username.trim().replace(/\/$/, '');
      const parts = clean.split('/').filter(Boolean);
      return parts[parts.length - 1] || username;
    } catch { return username; }
  }
  return username;
}

export default function AdminOrderDetail() {
  const { orderCode } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');

  const [order, setOrder] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumo');
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentOk, setResentOk] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resentEmailOk, setResentEmailOk] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);
  const [deliverModal, setDeliverModal] = useState(false);
  const [deliverEmail, setDeliverEmail] = useState(true);
  const [deliverSms, setDeliverSms] = useState(true);
  const [delivering, setDelivering] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/backoffice'); return; }
    fetchData();
  }, [orderCode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const orders = res.data.data;
      setAllOrders(orders);
      const found = orders.find(o => o.orderCode === orderCode);
      if (!found) { navigate('/backoffice/dashboard'); return; }
      setOrder(found);
      if (!found.viewedAt) {
        axios.post(`${API_URL}/admin/orders/${found.id}/viewed`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/backoffice');
      } else {
        navigate('/backoffice/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const customerOrders = allOrders.filter(o =>
    o.id !== order?.id && (
      o.customerEmail === order?.customerEmail ||
      (order?.customerPhone && o.customerPhone === order?.customerPhone)
    )
  );

  const totalGasto = [order, ...customerOrders]
    .filter(o => o?.paymentStatus === 'paid')
    .reduce((sum, o) => sum + parseFloat(o?.price || 0), 0);

  const handleMarkAsPaid = async () => {
    if (!window.confirm(`Marcar pedido ${order.orderCode} como PAGO?\nSerá enviado email de confirmação ao cliente.`)) return;
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch { alert('Erro ao marcar como pago'); }
  };

  const handleMarkAsDelivered = async () => {
    setDelivering(true);
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/mark-delivered`,
        { sendEmail: deliverEmail, sendSms: deliverSms },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeliverModal(false);
      fetchData();
    } catch { alert('Erro ao marcar como entregue'); }
    finally { setDelivering(false); }
  };

  const openEditModal = () => {
    setEditFields({
      customerName: order.customerName || '',
      customerEmail: order.customerEmail || '',
      customerPhone: order.customerPhone || '',
      mbwayPhone: order.mbwayPhone || '',
      paymentMethod: order.paymentMethod || '',
      multibancoEntity: order.multibancoEntity || '',
      multibancoReference: order.multibancoReference || '',
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/admin/orders/${order.id}`, editFields, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditModal(false);
      fetchData();
    } catch { alert('Erro ao guardar alterações'); }
    finally { setSaving(false); }
  };

  const handleDeliverySms = async () => {
    if (!window.confirm(`Enviar SMS de entrega para ${order.customerName}?`)) return;
    setSendingSms(true);
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/delivery-sms`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Erro ao enviar SMS');
    } finally {
      setSendingSms(false);
    }
  };

  const handleDeliveryWhatsApp = async () => {
    if (!window.confirm(`Enviar WhatsApp de entrega para ${order.customerName}?`)) return;
    setSendingWa(true);
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/delivery-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('WhatsApp enviado com sucesso!');
    } catch (e) {
      alert(e.response?.data?.message || 'Erro ao enviar WhatsApp');
    } finally {
      setSendingWa(false);
    }
  };

  const handleResendConfirmationEmail = async () => {
    if (!window.confirm(`Reenviar email de confirmação para ${order.customerEmail}?`)) return;
    setResendingEmail(true);
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/resend-confirmation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResentEmailOk(true);
      setTimeout(() => setResentEmailOk(false), 4000);
    } catch (e) {
      alert(e.response?.data?.message || 'Erro ao reenviar email');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleResendAutomation = async () => {
    if (!window.confirm(`Reenviar pedido ${order.orderCode} para a automação?`)) return;
    setResending(true);
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/resend-automation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResentOk(true);
      setTimeout(() => setResentOk(false), 4000);
    } catch (e) {
      alert(e.response?.data?.message || 'Erro ao reenviar');
    } finally {
      setResending(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API_URL}/admin/orders/${order.id}/notes`, { text: noteText }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNoteText('');
      fetchData();
    } catch { alert('Erro ao adicionar nota'); }
    finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Apagar esta nota?')) return;
    try {
      await axios.delete(`${API_URL}/admin/orders/${order.id}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch { alert('Erro ao apagar nota'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">A carregar...</div>
      </div>
    );
  }

  if (!order) return null;

  const isCartOrder = order.items && Array.isArray(order.items);
  const notes = order.notes || [];

  const paymentLabel = order.paymentMethod === 'mbway' ? 'MB Way'
    : order.paymentMethod === 'multibanco' ? 'Multibanco'
    : order.paymentMethod;

  const timelineSteps = [
    {
      label: 'Criado',
      date: order.createdAt,
      done: true,
      color: 'bg-gray-500',
    },
    {
      label: 'Pago',
      date: order.paymentStatus === 'paid' ? order.updatedAt : null,
      done: order.paymentStatus === 'paid',
      color: 'bg-green-500',
    },
    {
      label: 'Entregue',
      date: order.orderStatus === 'completed' ? order.updatedAt : null,
      done: order.orderStatus === 'completed',
      color: 'bg-blue-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/backoffice/dashboard')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition font-medium"
              >
                <ArrowLeft className="w-5 h-5" />
                Voltar
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-bold text-gray-900">{order.orderCode}</h1>
                <PaymentBadge status={order.paymentStatus} />
                <OrderBadge status={order.orderStatus} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {order.paymentStatus === 'pending' && (
                <button
                  onClick={handleMarkAsPaid}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl hover:bg-green-600 transition font-semibold text-sm"
                >
                  ✅ Marcar como Pago
                </button>
              )}
              {order.paymentStatus === 'paid' && order.orderStatus !== 'completed' && (
                <button
                  onClick={() => setDeliverModal(true)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-xl hover:bg-blue-600 transition font-semibold text-sm"
                >
                  📦 Marcar como Entregue
                </button>
              )}
              {order.paymentStatus === 'paid' && (
                order.remindersSent?.deliverySms ? (
                  <button disabled className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-teal-100 text-teal-700 border border-teal-200 cursor-not-allowed">
                    ✅ SMS Enviado
                  </button>
                ) : (
                  <button
                    onClick={handleDeliverySms}
                    disabled={sendingSms}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-teal-500 text-white hover:bg-teal-600 transition disabled:opacity-50"
                  >
                    📱 {sendingSms ? 'A enviar...' : 'SMS Entrega'}
                  </button>
                )
              )}
              {order.customerPhone && (
                <button
                  onClick={handleDeliveryWhatsApp}
                  disabled={sendingWa}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-green-500 text-white hover:bg-green-600 transition disabled:opacity-50"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {sendingWa ? 'A enviar...' : 'WA Entregue'}
                </button>
              )}
              {order.paymentStatus === 'paid' && (
                <button
                  onClick={handleResendConfirmationEmail}
                  disabled={resendingEmail}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition ${
                    resentEmailOk
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                  } disabled:opacity-50`}
                >
                  <Mail className="w-4 h-4" />
                  {resendingEmail ? 'A enviar...' : resentEmailOk ? 'Email Enviado!' : 'Reenviar Email'}
                </button>
              )}
              {order.paymentStatus === 'paid' && (
                <button
                  onClick={handleResendAutomation}
                  disabled={resending}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition ${
                    resentOk
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  } disabled:opacity-50`}
                >
                  <Send className="w-4 h-4" />
                  {resending ? 'A reenviar...' : resentOk ? 'Enviado!' : 'Reenviar Automação'}
                </button>
              )}
              <button
                onClick={openEditModal}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 transition"
              >
                <Pencil className="w-4 h-4" />
                Editar
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Coluna principal */}
          <div className="lg:col-span-2 space-y-5">

            {/* Timeline */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <p className="font-bold text-gray-900 mb-5">Timeline</p>
              <div className="flex items-center">
                {timelineSteps.map((step, idx) => (
                  <div key={idx} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${step.done ? step.color : 'bg-gray-200'}`}>
                        {step.done
                          ? <CheckCircle className="w-5 h-5 text-white" />
                          : <Clock className="w-4 h-4 text-gray-400" />}
                      </div>
                      <p className={`text-xs font-semibold mt-1.5 ${step.done ? 'text-gray-800' : 'text-gray-400'}`}>{step.label}</p>
                      {step.date && (
                        <p className="text-xs text-gray-400">{new Date(step.date).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      )}
                    </div>
                    {idx < timelineSteps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-3 mb-6 ${timelineSteps[idx + 1].done ? 'bg-green-400' : 'bg-gray-200'}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setActiveTab('resumo')}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition ${activeTab === 'resumo' ? 'bg-purple-600 text-white shadow' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                >
                  Resumo
                </button>
                <button
                  onClick={() => setActiveTab('notas')}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition flex items-center gap-2 ${activeTab === 'notas' ? 'bg-purple-600 text-white shadow' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
                >
                  Notas
                  {notes.length > 0 && (
                    <span className="bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {notes.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Tab Resumo */}
              {activeTab === 'resumo' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="font-bold text-gray-900">Produtos</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {isCartOrder ? order.items.map((item, idx) => (
                      <div key={idx} className="p-4 flex items-start gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 capitalize">
                            {item.platform} — {item.quantity.toLocaleString('fr-FR')} {item.serviceType === 'likes' ? 'Likes' : item.serviceType === 'views' ? 'Views' : 'Seguidores'}
                          </p>
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm text-gray-500">@{getDisplayName(item.username)}</p>
                            <CopyUsernameBtn username={item.username} />
                            {getProfileUrl(item.platform, item.username) && (
                              <a href={getProfileUrl(item.platform, item.username)} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-600">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          {item.selectedPosts?.map((post, pi) => (
                            <a key={pi} href={post.postUrl} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1">
                              <ExternalLink className="w-3 h-3" />
                              Post {pi + 1} — {post.quantity} {item.serviceType}
                            </a>
                          ))}
                        </div>
                        <p className="font-bold text-gray-900 flex-shrink-0">€{parseFloat(item.price).toFixed(2)}</p>
                      </div>
                    )) : (
                      <div className="p-4 flex items-start gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 capitalize">
                            {order.platform} — {order.packageType}
                          </p>
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm text-gray-500">@{getDisplayName(order.username)}</p>
                            <CopyUsernameBtn username={order.username} />
                            {getProfileUrl(order.platform, order.username) && (
                              <a href={getProfileUrl(order.platform, order.username)} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-600">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          {order.selectedPosts?.map((post, pi) => (
                            <a key={pi} href={post.postUrl} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1">
                              <ExternalLink className="w-3 h-3" />
                              Post {pi + 1} — {post.quantity} {order.serviceType}
                            </a>
                          ))}
                        </div>
                        <p className="font-bold text-gray-900 flex-shrink-0">€{parseFloat(order.price).toFixed(2)}</p>
                      </div>
                    )}
                    <div className="px-5 py-3 bg-gray-50 flex justify-between items-center">
                      <p className="font-bold text-gray-900">Total</p>
                      <p className="font-bold text-purple-600 text-lg">€{parseFloat(order.price).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Notas */}
              {activeTab === 'notas' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <p className="font-bold text-gray-900 mb-3">Adicionar nota interna</p>
                    <textarea
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Escreva uma nota sobre esta encomenda..."
                      rows={3}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500 resize-none transition"
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={addingNote || !noteText.trim()}
                      className="mt-2 flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl font-semibold text-sm hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                      {addingNote ? 'A guardar...' : 'Adicionar nota'}
                    </button>
                  </div>

                  {notes.length === 0 ? (
                    <div className="bg-white rounded-xl p-10 shadow-sm border border-gray-100 text-center text-gray-400">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Sem notas ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[...notes].reverse().map(note => (
                        <div key={note.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm text-gray-800 flex-1 whitespace-pre-wrap">{note.text}</p>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-gray-300 hover:text-red-500 transition flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(note.createdAt).toLocaleString('pt-PT')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar direita */}
          <div className="space-y-4">

            {/* Dados do cliente */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <p className="font-bold text-gray-900">Dados do Cliente</p>
                {customerOrders.length > 0 && (
                  <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2.5 py-1 rounded-full">
                    {customerOrders.length + 1} encomendas
                  </span>
                )}
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-900">{order.customerName}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-700 break-all">{order.customerEmail}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{formatPhone(order.customerPhone)}</p>
                </div>
                {order.taxId && (
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <p className="text-sm text-gray-700">NIF: {order.taxId}</p>
                  </div>
                )}
              </div>
              {totalGasto > 0 && (
                <div className="bg-purple-50 rounded-xl px-4 py-3 mb-4">
                  <p className="text-xs text-purple-500 mb-0.5">Total gasto (pago)</p>
                  <p className="font-bold text-purple-700 text-lg">€{totalGasto.toFixed(2)}</p>
                </div>
              )}
              {order.customerPhone && (
                <a
                  href={`https://wa.me/351${order.customerPhone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-xl font-semibold text-sm transition"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              )}
            </div>

            {/* Dados de Pagamento */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <p className="font-bold text-gray-900 mb-3">Pagamento</p>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-400">Data</p>
                  <p className="text-sm font-semibold text-gray-900">{new Date(order.createdAt).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-400">Método</p>
                  <p className="text-sm font-semibold text-gray-900">{paymentLabel}</p>
                </div>
                {order.mbwayPhone && order.paymentMethod === 'mbway' && (
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">Nº MB Way</p>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-gray-900">{formatPhone(order.mbwayPhone)}</p>
                      {order.customerPhone && (
                        order.mbwayPhone.replace(/\D/g, '') === order.customerPhone.replace(/\D/g, '')
                          ? <span title="Igual ao número do cliente">✅</span>
                          : <span title="Diferente do número do cliente">⚠️</span>
                      )}
                    </div>
                  </div>
                )}
                {order.paymentMethod === 'multibanco' && order.multibancoEntity && (
                  <>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-400">Entidade</p>
                      <p className="text-sm font-semibold text-gray-900">{order.multibancoEntity}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-400">Referência</p>
                      <p className="text-sm font-semibold text-gray-900">{order.multibancoReference}</p>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-base font-bold text-purple-600">€{parseFloat(order.price).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Histórico de encomendas do cliente */}
            {customerOrders.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <p className="font-bold text-gray-900 mb-3">Outras Encomendas</p>
                <div className="space-y-2">
                  {customerOrders.map(o => (
                    <button
                      key={o.id}
                      onClick={() => navigate(`/backoffice/order/${o.orderCode}`)}
                      className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-purple-200 hover:bg-purple-50 transition"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-purple-700">{o.orderCode}</p>
                        <p className="text-xs font-bold text-gray-900">€{parseFloat(o.price).toFixed(2)}</p>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500">{new Date(o.createdAt).toLocaleDateString('pt-PT')}, {new Date(o.createdAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</p>
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                          o.orderStatus === 'completed' ? 'bg-blue-100 text-blue-700'
                          : o.paymentStatus === 'paid' ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                        }`}>
                          {o.orderStatus === 'completed' ? 'Entregue' : o.paymentStatus === 'paid' ? 'Pago' : 'Pendente'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modal editar pedido */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <p className="font-bold text-gray-900 text-base mb-4">Editar Pedido</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Nome</label>
                <input value={editFields.customerName} onChange={e => setEditFields(p => ({...p, customerName: e.target.value}))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Email</label>
                <input value={editFields.customerEmail} onChange={e => setEditFields(p => ({...p, customerEmail: e.target.value}))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Telefone</label>
                <input value={editFields.customerPhone} onChange={e => setEditFields(p => ({...p, customerPhone: e.target.value}))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Método de Pagamento</label>
                <select value={editFields.paymentMethod} onChange={e => setEditFields(p => ({...p, paymentMethod: e.target.value}))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500">
                  <option value="mbway">MB Way</option>
                  <option value="multibanco">Multibanco</option>
                </select>
              </div>
              {editFields.paymentMethod === 'mbway' && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Nº MB Way</label>
                  <input value={editFields.mbwayPhone} onChange={e => setEditFields(p => ({...p, mbwayPhone: e.target.value}))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
                </div>
              )}
              {editFields.paymentMethod === 'multibanco' && (
                <>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Entidade</label>
                    <input value={editFields.multibancoEntity} onChange={e => setEditFields(p => ({...p, multibancoEntity: e.target.value}))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Referência</label>
                    <input value={editFields.multibancoReference} onChange={e => setEditFields(p => ({...p, multibancoReference: e.target.value}))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditModal(false)}
                className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={handleSaveEdit} disabled={saving}
                className="flex-1 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition disabled:opacity-50">
                {saving ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal marcar como entregue */}
      {deliverModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 shadow-xl w-80">
            <p className="font-bold text-gray-900 text-base mb-1">Marcar como Entregue</p>
            <p className="text-xs text-gray-400 mb-4">Escolhe o que enviar ao cliente:</p>
            <div className="space-y-3 mb-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliverEmail}
                  onChange={e => setDeliverEmail(e.target.checked)}
                  className="w-4 h-4 accent-purple-600"
                />
                <span className="text-sm text-gray-700">✉️ Email de entrega</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deliverSms}
                  onChange={e => setDeliverSms(e.target.checked)}
                  className="w-4 h-4 accent-purple-600"
                />
                <span className="text-sm text-gray-700">📱 SMS de entrega</span>
              </label>
              {!deliverEmail && !deliverSms && (
                <p className="text-xs text-orange-500">Nenhuma notificação será enviada ao cliente.</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDeliverModal(false)}
                className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleMarkAsDelivered}
                disabled={delivering}
                className="flex-1 py-2 rounded-xl bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition disabled:opacity-50"
              >
                {delivering ? 'A marcar...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
